import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Shield, AlertTriangle, ChevronRight, CheckCircle, Lightbulb } from "lucide-react";
import { API, scrollToTop, setCanonical } from "../lib/config";
import { useSettings, getAffiliate } from "../lib/useSettings";
import { Nav, Footer, HeroBanner, CookieConsent, QuizTeaser } from "../components/layout";
import { ChatBot, ExitIntentPopup } from "../components/chat";
import { MiniQuiz } from "../components/quiz";
import { AffiliateBanner, AdSensePlaceholder, AdSenseOnly } from "../components/ads";

export default function CoverageTypePage() {
  const { slug } = useParams();
  const [coverage, setCoverage] = useState(null);
  const settings = useSettings();

  useEffect(() => {
    axios.get(`${API}/coverage/${slug}`).then(r => {
      const c = r.data;
      setCoverage(c);
      setCanonical(`/coverage/${slug}`);
      document.title = `${c.name} Explained | QuotesByCity`;
      const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
      meta.name = "description";
      meta.content = `${c.name}: ${c.tagline}. Learn what it covers, common myths, real-life scenarios, and tips.`;
      if (!meta.parentNode) document.head.appendChild(meta);
      const schemas = [
        ["cov-article", { "@context": "https://schema.org", "@type": "Article", "headline": `${c.name} Explained`, "description": c.tagline, "author": { "@type": "Organization", "name": "QuotesByCity", "url": "https://www.quotesbycity.com" }, "publisher": { "@type": "Organization", "name": "QuotesByCity", "url": "https://www.quotesbycity.com" } }],
        ["cov-service", { "@context": "https://schema.org", "@type": "Service", "name": `${c.name} Education`, "description": `${c.tagline}. Free educational guide explaining ${c.name} for US drivers.`, "provider": { "@type": "Organization", "name": "QuotesByCity", "url": "https://www.quotesbycity.com" }, "serviceType": "Auto Insurance Education", "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" }, "areaServed": "United States" }],
        ["cov-faq", { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [
          { "@type": "Question", "name": `What is ${c.name}?`, "acceptedAnswer": { "@type": "Answer", "text": c.description }},
          { "@type": "Question", "name": `Is ${c.name} required by law?`, "acceptedAnswer": { "@type": "Answer", "text": `Requirements for ${c.name} vary by state. Check your state's specific requirements and consult a licensed insurance professional for advice specific to your situation.` }},
          { "@type": "Question", "name": `What is a common myth about ${c.name}?`, "acceptedAnswer": { "@type": "Answer", "text": c.myth }},
        ]}],
        ["cov-bread", { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.quotesbycity.com" },
          { "@type": "ListItem", "position": 2, "name": "Coverage Types", "item": "https://www.quotesbycity.com/coverage/liability" },
          { "@type": "ListItem", "position": 3, "name": c.name, "item": `https://www.quotesbycity.com/coverage/${slug}` },
        ]}],
        ["cov-speak", { "@context": "https://schema.org", "@type": "WebPage", "name": c.name, "description": `${c.name} is ${c.tagline}. ${c.description}`, "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro", "[data-testid='coverage-description']", "[data-testid='coverage-myth']"] }, "url": `https://www.quotesbycity.com/coverage/${slug}` }],
      ];
      schemas.forEach(([id, schema]) => {
        let el = document.getElementById(`schema-${id}`);
        if (!el) { el = document.createElement("script"); el.id = `schema-${id}`; el.type = "application/ld+json"; document.head.appendChild(el); }
        el.textContent = JSON.stringify(schema);
      });
    }).catch(() => {});
    return () => { ["cov-article", "cov-service", "cov-faq", "cov-bread", "cov-speak"].forEach(id => { const el = document.getElementById(`schema-${id}`); if (el) el.remove(); }); };
  }, [slug]);

  if (!coverage) return <div className="min-h-screen bg-[#FAFAFA]"><Nav /><div className="max-w-4xl mx-auto px-4 py-20 text-center text-slate-400">Loading...</div></div>;

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <HeroBanner title={coverage.name} subtitle={coverage.tagline} badge="Coverage Type Explained" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* GEO: Definition-lead sentence for AI engine citation */}
        <p className="speakable-intro text-sm text-sky-700 font-medium mb-6">
          {coverage.name} is a type of auto insurance that {coverage.tagline.toLowerCase()} — QuotesByCity provides a free educational guide to help US drivers understand what it covers, common myths, and when they need it.
        </p>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* MAIN CONTENT */}
          <div className="flex-1 min-w-0 space-y-8">

            {/* DESCRIPTION */}
            <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="coverage-description">
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>What is {coverage.name}?</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{coverage.description}</p>
            </section>

            {/* MYTH BUSTER */}
            <section className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="coverage-myth">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-red-500 mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-bold text-red-800 text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Common Myth</h3>
                  <p className="text-sm text-red-700">{coverage.myth}</p>
                </div>
              </div>
            </section>

            {/* REAL-LIFE SCENARIOS */}
            <section data-testid="coverage-scenarios">
              <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Real-Life Scenarios</h2>
              <div className="space-y-3">
                {coverage.scenarios?.map((s, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
                    <div className="w-7 h-7 bg-sky-100 rounded-lg flex items-center justify-center shrink-0"><span className="text-xs font-bold text-sky-700">{i + 1}</span></div>
                    <p className="text-sm text-slate-600">{s}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* TIPS */}
            <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-6" data-testid="coverage-tips">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb size={18} className="text-emerald-600" />
                <h3 className="font-bold text-emerald-800 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Key Things to Know</h3>
              </div>
              <div className="space-y-2">
                {coverage.tips?.map((t, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-emerald-800">{t}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* MINI QUIZ */}
            <MiniQuiz context={slug} />

            {/* CTA */}
            <div className="bg-sky-700 rounded-xl p-6 text-center" data-testid="coverage-cta">
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Want to see what {coverage.name} costs?</h3>
              <p className="text-sm text-sky-200 mb-4">Compare rates from top providers — free and no obligation.</p>
              <a href={getAffiliate(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-sky-700 rounded-xl px-6 py-3 text-sm font-semibold hover:bg-sky-50 transition-all" data-testid="coverage-compare-btn">
                Compare Rates <ArrowRight size={14} />
              </a>
            </div>

            {/* MOBILE ADS */}
            <div className="lg:hidden">
              <AdSenseOnly />
            </div>

            {/* QUIZ TEASER */}
            <QuizTeaser />

            {/* OTHER COVERAGE TYPES */}
            <section data-testid="other-coverage">
              <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Other Coverage Types</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["Liability", "liability"], ["Collision", "collision"], ["Comprehensive", "comprehensive"], ["Uninsured Motorist", "uninsured-underinsured"], ["Gap Insurance", "gap-insurance"], ["Full Coverage Myth", "full-coverage-myth"]].filter(([_, s]) => s !== slug).map(([name, s]) => (
                  <Link key={s} to={`/coverage/${s}`} onClick={scrollToTop} className="bg-white rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 hover:text-sky-700 hover:border-sky-300 transition-all flex items-center justify-between">
                    {name} <ChevronRight size={14} className="text-slate-400" />
                  </Link>
                ))}
              </div>
            </section>
          </div>

          {/* SIDEBAR */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6 hidden lg:block" data-testid="sidebar">
            <div className="lg:sticky lg:top-20 space-y-6">
              <AdSensePlaceholder size="large" />
            </div>
          </aside>
        </div>
      </div>
      <Footer />
      <ChatBot />
      <ExitIntentPopup />
      <CookieConsent />
    </div>
  );
}
